import { Fragment, useRef, useState } from 'react'
import { Upload, Play, RefreshCw, CheckCircle, XCircle, Loader2, Clock, FileText, Trash2, Square } from 'lucide-react'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import { useIngestionJobs, useProcessJob, useRunIngestion, useSubmitJob, useUploadUrl, useGenerateViews, useViewCoverage, useDeleteJob, useStopTask, useBackfillStatus } from '@/hooks/useIngestion'
import { useAdminPubAuthorities } from '@/hooks/useAdminDatabase'
import type { IngestionJob } from '@/api/adminService'
import LoadingSpinner from '@/components/admin/shared/LoadingSpinner'
import EmptyState from '@/components/admin/shared/EmptyState'

// ─── Phase badge ──────────────────────────────────────────────────────────────

const PHASE_CONFIG: Record<IngestionJob['phase'], { label: string; color: string; icon: React.FC<{ className?: string }> }> = {
  preparing:  { label: 'Preparing',  color: 'bg-gray-100 text-gray-600',   icon: Loader2 },
  submitting: { label: 'Submitting', color: 'bg-blue-50 text-blue-600',    icon: Loader2 },
  embedding:  { label: 'Embedding',  color: 'bg-purple-50 text-purple-600', icon: Loader2 },
  processing: { label: 'Processing', color: 'bg-yellow-50 text-yellow-600', icon: Loader2 },
  completed:  { label: 'Completed',  color: 'bg-green-50 text-green-700',  icon: CheckCircle },
  failed:     { label: 'Failed',     color: 'bg-red-50 text-red-600',      icon: XCircle },
}

function PhaseBadge({ phase, error }: { phase: IngestionJob['phase']; error?: string | null }) {
  const { label, color, icon: Icon } = PHASE_CONFIG[phase] ?? PHASE_CONFIG.preparing
  const spinning = ['preparing', 'submitting', 'embedding', 'processing'].includes(phase)
  return (
    <span
      className={clsx('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium', color)}
      title={phase === 'failed' && error ? error : undefined}
    >
      <Icon className={clsx('w-3 h-3', spinning && 'animate-spin')} />
      {label}
    </span>
  )
}

// ─── Upload + run form ────────────────────────────────────────────────────────

function RunIngestionForm() {
  const fileRef               = useRef<HTMLInputElement>(null)
  const [file, setFile]       = useState<File | null>(null)
  const [fileKey, setFileKey] = useState('')
  const [options, setOptions] = useState({ process_as_standards: false })
  const [status, setStatus]   = useState<'idle' | 'uploading' | 'running' | 'done' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const { mutateAsync: getUploadUrl } = useUploadUrl()
  const { mutateAsync: run }          = useRunIngestion()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!file && !fileKey) return
    setStatus('idle')
    setMessage('')

    try {
      let key = fileKey

      if (file) {
        setStatus('uploading')
        const { upload_url, file_key } = await getUploadUrl(file.name)
        await fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': 'text/csv' } })
        key = file_key
      }

      setStatus('running')
      const result = await run({ file_key: key, ...options })
      setStatus('done')
      setMessage(`Task launched: ${result.task_arn.split('/').pop()}`)
      setFile(null)
      setFileKey('')
      if (fileRef.current) fileRef.current.value = ''
    } catch (err: unknown) {
      setStatus('error')
      setMessage(err instanceof Error ? err.message : 'Failed to start ingestion')
    }
  }

  const busy = status === 'uploading' || status === 'running'

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Start New Ingestion Run</h2>

      {/* File upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
            <Upload className="w-4 h-4 text-gray-400" />
            {file ? file.name : 'Choose file…'}
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setFileKey('') }}
            />
          </label>
          <span className="self-center text-xs text-gray-400">or enter S3 key</span>
          <input
            type="text"
            value={fileKey}
            onChange={(e) => { setFileKey(e.target.value); setFile(null); if (fileRef.current) fileRef.current.value = '' }}
            placeholder="staging/updates-2026-04-22.csv"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Options */}
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={options.process_as_standards}
            onChange={(e) => setOptions((o) => ({ ...o, process_as_standards: e.target.checked }))}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Process as standards (PDF path)
        </label>
      </div>

      {/* Status message */}
      {message && (
        <p className={clsx(
          'text-sm px-3 py-2 rounded-md',
          status === 'error' ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200',
        )}>
          {message}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || (!file && !fileKey.trim())}
        className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
      >
        {busy
          ? <><Loader2 className="w-4 h-4 animate-spin" />{status === 'uploading' ? 'Uploading…' : 'Launching…'}</>
          : <><Play className="w-4 h-4" />Run Ingestion</>
        }
      </button>
    </form>
  )
}

// ─── Job history ──────────────────────────────────────────────────────────────

function JobHistory() {
  const { data: jobs, isLoading, error, refetch, isFetching } = useIngestionJobs()
  const { mutate: submitJob,  isPending: isSubmitting,  variables: submittingKey  } = useSubmitJob()
  const { mutate: processJob, isPending: isProcessing,  variables: processingKey  } = useProcessJob()
  const { mutate: deleteJob,  isPending: isDeleting,    variables: deletingKey    } = useDeleteJob()

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Recent Jobs</h2>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <RefreshCw className={clsx('w-3.5 h-3.5', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <p className="text-red-500 text-sm py-8 text-center">Failed to load jobs.</p>
      ) : !jobs?.length ? (
        <EmptyState message="No ingestion jobs found" />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">CSV</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Chunks</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Completed</th>
                <th className="px-4 py-3">Bedrock Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.map((job) => (
                <Fragment key={job.run_id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{job.csv_name}</td>
                    <td className="px-4 py-3"><PhaseBadge phase={job.phase} error={job.error} /></td>
                    <td className="px-4 py-3 text-gray-600">{job.total_chunks > 0 ? job.total_chunks.toLocaleString() : '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {format(new Date(job.started_at), 'MMM d, HH:mm')}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {job.completed_at
                        ? format(new Date(job.completed_at), 'MMM d, HH:mm')
                        : <span className="text-gray-300 flex items-center gap-1"><Clock className="w-3 h-3" />Pending</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                      {job.bedrock_status ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {job.phase === 'failed' && job.has_chunks && (
                          <button
                            onClick={() => submitJob(job.manifest_key)}
                            disabled={isSubmitting && submittingKey === job.manifest_key}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                          >
                            {isSubmitting && submittingKey === job.manifest_key
                              ? <><Loader2 className="w-3 h-3 animate-spin" />Starting…</>
                              : <><Play className="w-3 h-3" />Re-submit</>
                            }
                          </button>
                        )}
                        <button
                          onClick={() => processJob(job.manifest_key)}
                          disabled={isProcessing && processingKey === job.manifest_key}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-yellow-300 transition-colors"
                        >
                          {isProcessing && processingKey === job.manifest_key
                            ? <><Loader2 className="w-3 h-3 animate-spin" />Starting…</>
                            : <><Play className="w-3 h-3" />Process</>
                          }
                        </button>
                        <button
                          onClick={() => { if (confirm('Delete this job and all its S3 files?')) deleteJob({ run_id: job.run_id, manifest_key: job.manifest_key }) }}
                          disabled={isDeleting && deletingKey?.run_id === job.run_id}
                          className="flex items-center justify-center w-7 h-7 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 transition-colors"
                          title="Delete job"
                        >
                          {isDeleting && deletingKey?.run_id === job.run_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Trash2 className="w-3 h-3" />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                  {job.error && (
                    <tr>
                      <td colSpan={7} className="px-4 py-2 bg-red-50 border-t-0 text-xs text-red-600">
                        <span className="font-medium">Error:</span> {job.error}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── View backfill ────────────────────────────────────────────────────────────

function ViewBackfillCard() {
  const { mutate, isPending, error } = useGenerateViews()
  const { data: coverage, isLoading: coverageLoading, refetch: refetchCoverage } = useViewCoverage()
  const { data: backfillStatus } = useBackfillStatus()
  const { mutate: stopTask, isPending: isStopping } = useStopTask()
  const { data: pubAuthorities } = useAdminPubAuthorities()
  const [selectedPubAuth, setSelectedPubAuth] = useState('')
  const [missingOnly, setMissingOnly] = useState(true)

  const taskArn = backfillStatus?.task_arn ?? null

  function handleRun(dry_run: boolean) {
    mutate(
      { dry_run, pub_authority_uuid: selectedPubAuth || undefined, missing_only: missingOnly },
      { onSuccess: () => refetchCoverage() },
    )
  }

  function handleStop() {
    if (!taskArn) return
    stopTask(taskArn)
  }

  const pct = coverage && coverage.total_regulations > 0
    ? Math.round((coverage.views_generated / coverage.total_regulations) * 100)
    : null

  return (
    <div className="border border-gray-200 rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Regulation View Backfill</h2>
          </div>
          <p className="text-xs text-gray-500 max-w-lg">
            Generates and uploads <code className="bg-gray-100 px-1 rounded">views/&#123;uuid&#125;.json</code> to S3 for every regulation document in RDS.
            Required before the document viewer works in production. Stop the job anytime via ECS console.
          </p>
          {taskArn && (
            <p className="mt-2 text-xs text-amber-600 font-mono truncate max-w-sm">Running: {taskArn.split('/').pop()}</p>
          )}
          {error && (
            <p className="mt-2 text-xs text-red-600">{String(error)}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={selectedPubAuth}
            onChange={e => setSelectedPubAuth(e.target.value)}
            disabled={!!taskArn}
            className="text-xs border border-gray-200 rounded px-2 py-1.5 text-gray-600 bg-white disabled:opacity-50 max-w-[180px]"
          >
            <option value="">All authorities</option>
            {pubAuthorities?.map(pa => (
              <option key={pa.uuid} value={pa.uuid}>{pa.name}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={missingOnly}
              onChange={e => setMissingOnly(e.target.checked)}
              disabled={!!taskArn}
              className="rounded border-gray-300"
            />
            Missing only
          </label>
          {taskArn ? (
            <button
              onClick={handleStop}
              disabled={isStopping}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
            >
              {isStopping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
              Stop
            </button>
          ) : (
            <>
              <button
                onClick={() => handleRun(true)}
                disabled={isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Dry run
              </button>
              <button
                onClick={() => handleRun(false)}
                disabled={isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand text-white rounded hover:bg-brand/90 disabled:opacity-50"
              >
                {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Run backfill
              </button>
            </>
          )}
        </div>
      </div>

      {/* Coverage stats */}
      <div className="border-t border-gray-100 pt-3">
        {coverageLoading ? (
          <div className="flex items-center gap-1.5 text-xs text-gray-400"><Loader2 className="w-3 h-3 animate-spin" />Loading coverage…</div>
        ) : coverage ? (
          <div className="flex items-center gap-6">
            <div className="text-xs text-gray-500">
              <span className="font-semibold text-gray-800">{coverage.views_generated.toLocaleString()}</span> / {coverage.total_regulations.toLocaleString()} views generated
            </div>
            {coverage.missing > 0 && (
              <div className="text-xs text-amber-600 font-medium">{coverage.missing.toLocaleString()} missing</div>
            )}
            {pct !== null && (
              <div className="flex items-center gap-2 flex-1 max-w-xs">
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-brand rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-gray-500 tabular-nums">{pct}%</span>
              </div>
            )}
            <button onClick={() => refetchCoverage()} className="text-xs text-gray-400 hover:text-gray-600 ml-auto">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function IngestionPage() {
  return (
    <div className="space-y-8">
      <RunIngestionForm />
      <ViewBackfillCard />
      <JobHistory />
    </div>
  )
}
